import { func, required, validationContext } from 'validx'
import { action, computed, observable } from 'mobx'
import {
  type ExtensionBillingInfo,
  type ExtensionRegularBilling,
} from '@/features/inventory/ExtensionBillingInfo'
import { type InventoryItemExtensionBillingType } from '@/features/inventory/InventoryItem'

export type GetBillingType = () => InventoryItemExtensionBillingType

/**
 * Edits a client charge and pro payout pair for an extension.
 */
export class RegularEditorViewModel {
  /**
   * The client amount for the Inventory Item. This is the amount the client will
   * be charged.
   */
  @observable accessor clientAmount = 0

  /**
   * The pro payout amount for the Inventory Item. This is the amount the pro will
   * receive.
   */
  @observable accessor proPayoutAmount = 0

  readonly validation = validationContext(this, {
    clientAmount: [
      required('This field is required.'),
      func(({ value }) =>
        this.isFlat
          ? value > 0 || 'The amount must be greater than zero.'
          : (value > 0 && value <= 100) ||
            'The percentage should be between 0 and 100',
      ),
    ],
    proPayoutAmount: [
      required('This field is required.'),
      func(({ value }) =>
        this.isFlat
          ? value > 0 || 'The amount must be greater than zero.'
          : (value > 0 && value <= 100) ||
            'The percentage should be between 0 and 100',
      ),
    ],
  })

  constructor(private readonly _billingType: GetBillingType) {}

  /**
   * Whether we are using a flat fee or percentage
   */
  @computed
  get isFlat() {
    return this._billingType() === 'FlatFee'
  }

  /**
   * Whether the data is valid
   */
  validate(): boolean {
    return this.validation.reset().validate().isValid
  }

  /**
   * Restores the UI from saved data
   */
  @action.bound
  hydrate(values: ExtensionBillingInfo): void {
    const { clientChargeAmount, proPayoutAmount } =
      values as ExtensionRegularBilling

    this.clientAmount = clientChargeAmount
    this.proPayoutAmount = proPayoutAmount
  }

  /**
   * Produces the values needed to submit to the api
   */
  toValues(): ExtensionRegularBilling {
    return {
      type: 'Regular',
      clientChargeAmount: this.clientAmount,
      proPayoutAmount: this.proPayoutAmount,
    }
  }

  /**
   * Clears the data
   */
  @action.bound
  reset() {
    this.clientAmount = 0
    this.proPayoutAmount = 0
    this.validation.reset()
  }
}
